import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Avatar } from "@nestcoinco/hyper-api-gateway-api-client";
import { useAtom, useAtomValue } from "jotai";

import "./AvatarSection.scss";
import CircularButton from "../../Buttons/CircularButton/CircularButton";
import ImageUserIcon from "../../../images/user-filled.png";
import AvatarModal from "../AvatarModal/AvatarModal";
import CameraOutlineIcon from "../../icons/CameraOutlineIcon";
import { FunctionWithVoidReturn } from "../../../utils/constants/generic-types";
import {
  userCustomAvatarAtom,
  userProfileAtom,
} from "../../../jotai/atoms/user";
import { handleFetchAvatars } from "../../../api-clients/usersAvatarsClient";
import { handleUpdateUser } from "../../../api-clients/userAccountClient";

interface AvatarSectionProps {
  EditIcon?: FunctionComponent<React.SVGProps<SVGSVGElement>>;
  containerClass?: string;
  handleEdit?: FunctionWithVoidReturn;
}

function AvatarSection({
  EditIcon = CameraOutlineIcon,
  containerClass = "",
  handleEdit,
}: AvatarSectionProps) {
  const [userProfile, setUserProfile] = useAtom(userProfileAtom);
  const [isOpen, setIsOpen] = useState(false);
  const [avatars, setAvatars] = useState<Avatar[]>();
  const userCustomAvatar = useAtomValue(userCustomAvatarAtom);

  const handleOpenSelector = () => {
    setIsOpen((prev) => !prev);
  };

  const updateAvatar = useCallback(
    async (avatarId: string) => {
      const user = await handleUpdateUser({ avatarId });
      if (!user) return;

      setUserProfile(user);
    },
    [setUserProfile]
  );

  // Fetch pre-uploaded avatars from BE
  useEffect(() => {
    (async () => {
      const userAvatars = (await handleFetchAvatars())?.records;
      userAvatars && setAvatars(userAvatars);
    })();
  }, []);

  // Assign a random avatar from the backend to new users upon sign-up
  useEffect(() => {
    if (!avatars || !avatars.length || !userProfile || !!userProfile?.avatarId)
      return;

    const randomIndex = Math.floor(Math.random() * avatars.length);
    const randomAvatarId = avatars[randomIndex].id;
    updateAvatar(randomAvatarId);
  }, [avatars, userProfile, updateAvatar]);

  return (
    <section className={`avatar-section ${containerClass}`}>
      <CircularButton
        color="inherit"
        image={userCustomAvatar || userProfile?.avatar?.url || ImageUserIcon}
        buttonClass="user-image-container"
        imageClass="profile-avatar"
        onClick={handleEdit ?? handleOpenSelector}
      />
      <CircularButton
        color="#DCD3FD"
        image={<EditIcon />}
        buttonClass="user-image-edit-button"
        onClick={handleEdit ?? handleOpenSelector}
      />
      <AvatarModal isOpen={isOpen} setIsOpen={setIsOpen} avatars={avatars} />
    </section>
  );
}

export default AvatarSection;
